(() => {
  const roots = document.querySelectorAll("[data-nashe-judgeme-api-reviews]");
  if (!roots.length) return;

  const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  }[char]));

  const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

  const decodeHtml = (value) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value ?? "");
    return textarea.value;
  };

  const htmlText = (value) => clean(decodeHtml(String(value ?? "").replace(/<[^>]*>/g, " ")));

  const stars = (rating) => {
    const score = Math.max(0, Math.min(5, Math.round(Number(rating) || 5)));
    return "★★★★★".slice(0, score) + "☆☆☆☆☆".slice(0, 5 - score);
  };

  const parseDateValue = (value) => {
    if (!value) return 0;
    const direct = new Date(value);
    if (!Number.isNaN(direct.getTime())) return direct.getTime();
    const match = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!match) return 0;
    const [, day, month, year, hour = "00", minute = "00", second = "00"] = match;
    return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)).getTime();
  };

  const dateText = (value) => {
    const timestamp = parseDateValue(value);
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const firstPicture = (review) => {
    const pictures = Array.isArray(review.pictures_urls) ? review.pictures_urls : [];
    const first = pictures[0];
    if (!first) return "";
    if (typeof first === "string") return first;
    return first.original || first.huge || first.url || first.href || first.small || first.compact || first.thumb || "";
  };

  const productImage = (review) => review.product_variant_image_url || review.product_pictures_url || "";

  const photoKey = (photo) => {
    if (!photo) return "";
    try {
      const url = new URL(photo, window.location.origin);
      return `${url.hostname}${url.pathname}`.toLowerCase();
    } catch {
      return String(photo).split("?")[0].toLowerCase();
    }
  };

  const imageFingerprint = (photo) => {
    if (!photo) return "";
    try {
      const url = new URL(photo, window.location.origin);
      const name = (url.pathname.split("/").pop() || "").toLowerCase();
      const baseName = name.replace(/__(?:original|small|compact|thumbnail)\.[a-z0-9]+$/i, "");
      const dashIndex = baseName.indexOf("-");
      if (dashIndex > 0 && /^\d+$/.test(baseName.slice(0, dashIndex).replaceAll("__", ""))) return baseName.slice(dashIndex + 1);
      return baseName;
    } catch {
      return String(photo).split("?")[0].toLowerCase();
    }
  };

  const reviewQuality = (review) => {
    let score = 0;
    if (review.productTitle && review.productTitle !== "NASHE") score += 4;
    if (review.productUrl && review.productUrl.includes("/products/")) score += 3;
    if (review.body && !/^photo review from/i.test(review.body)) score += 2;
    if (review.verified) score += 1;
    return score;
  };

  const uniqueReviews = (reviews) => {
    const byKey = new Map();
    for (const review of reviews) {
      const key = review.photo ? imageFingerprint(review.photo) : review.id;
      const existing = byKey.get(key);
      if (!existing || reviewQuality(review) > reviewQuality(existing)) byKey.set(key, review);
    }
    return Array.from(byKey.values());
  };

  const largePhotoUrl = (photo) => {
    if (!photo || !String(photo).includes("review-images.judgeme.com")) return photo;
    try {
      const url = new URL(photo, window.location.origin);
      url.searchParams.set("quality", "90");
      url.searchParams.set("width", "900");
      return url.href;
    } catch {
      return photo;
    }
  };

  const hashSeed = (value) => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };

  const seededRandom = (seed) => {
    let value = seed || 1;
    return () => {
      value += 0x6D2B79F5;
      let next = value;
      next = Math.imul(next ^ (next >>> 15), next | 1);
      next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
      return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
    };
  };

  const shuffled = (items, seedText) => {
    const output = [...items];
    const random = seededRandom(hashSeed(seedText));
    for (let index = output.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [output[index], output[swapIndex]] = [output[swapIndex], output[index]];
    }
    return output;
  };

  const rotationSeed = (root, suffix) => {
    const now = new Date();
    const day = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
    return `${day}:${window.location.pathname}:${root.id || "reviews"}:${suffix}`;
  };

  const sortReviewsByDate = (reviews) => [...reviews].sort((a, b) => {
    const dateDiff = (b.timestamp || 0) - (a.timestamp || 0);
    if (dateDiff) return dateDiff;
    return String(b.id || "").localeCompare(String(a.id || ""));
  });

  const isProductReview = (review) => {
    const url = clean(review.product_url);
    return review.product_in_store !== false && (url.startsWith("/products/") || url.includes("/products/"));
  };

  const normalize = (review) => ({
    id: review.uuid || `${review.reviewer_name}-${review.created_at}`,
    author: clean(review.reviewer_name) || "Verified buyer",
    initial: clean(review.reviewer_initial || review.reviewer_name?.[0]) || "N",
    rating: Number(review.rating) || 5,
    body: clean(review.body || review.body_html?.replace(/<[^>]*>/g, "")) || (firstPicture(review) ? "Photo review." : ""),
    verified: Boolean(review.verified_buyer),
    date: dateText(review.created_at),
    timestamp: parseDateValue(review.created_at),
    productTitle: clean(review.product_title) || "NASHE",
    productUrl: clean(review.product_url) || "/collections/all-products",
    productImage: productImage(review),
    photo: largePhotoUrl(firstPicture(review)),
  });

  const normalizePhotoReviewNode = (node) => {
    const ratingNode = node.querySelector(".jdgm-rev__rating");
    const productLink = node.querySelector(".jdgm-rev__prod-link");
    const photoNode = node.querySelector(".jdgm-rev__pic-img");
    const rawPhoto = photoNode?.getAttribute("data-src") || photoNode?.getAttribute("src") || "";
    const photo = rawPhoto ? largePhotoUrl(new URL(rawPhoto, window.location.origin).href) : "";
    const timestamp = node.querySelector(".jdgm-rev__timestamp")?.getAttribute("data-content") || "";
    const productUrl = productLink?.getAttribute("href") || "/pages/reviews";
    const productTitle = clean(productLink?.textContent) || "NASHE";

    return {
      id: node.getAttribute("data-review-id") || photo || `${clean(node.querySelector(".jdgm-rev__author")?.textContent)}-${timestamp}`,
      author: clean(node.querySelector(".jdgm-rev__author")?.textContent) || "Verified buyer",
      initial: clean(node.querySelector(".jdgm-rev__icon")?.textContent || node.querySelector(".jdgm-rev__author")?.textContent?.[0]) || "N",
      rating: Number(ratingNode?.getAttribute("data-score")) || 5,
      body: clean(node.querySelector(".jdgm-rev__body")?.textContent) || "Photo review.",
      verified: node.getAttribute("data-verified-buyer") === "true" || Boolean(node.querySelector(".jdgm-rev__buyer-badge")),
      date: dateText(timestamp),
      timestamp: parseDateValue(timestamp),
      productTitle,
      productUrl: productUrl.startsWith("http") ? productUrl : new URL(productUrl, window.location.origin).href,
      productImage: "",
      photo,
    };
  };

  const normalizeLegacyReviewNode = (node) => {
    const ratingNode = node.querySelector(".jdgm-rev__rating");
    const productLink = node.querySelector(".jdgm-rev__prod-link");
    const photoNode = node.querySelector(".jdgm-rev__pic-img");
    const rawPhoto = photoNode?.getAttribute("data-src") || photoNode?.getAttribute("src") || "";
    const photo = rawPhoto ? largePhotoUrl(new URL(rawPhoto, window.location.origin).href) : "";
    const timestamp = node.querySelector(".jdgm-rev__timestamp")?.getAttribute("data-content") || "";
    const productUrl = productLink?.getAttribute("href") || "";
    const productTitle = clean(productLink?.textContent) || "NASHE";
    const author = clean(node.querySelector(".jdgm-rev__author")?.textContent) || "Verified buyer";

    return {
      id: node.getAttribute("data-review-id") || `${author}-${timestamp}-${productTitle}`,
      author,
      initial: clean(node.querySelector(".jdgm-rev__icon")?.textContent || author.slice(0, 1)) || "N",
      rating: Number(ratingNode?.getAttribute("data-score")) || 5,
      body: clean(node.querySelector(".jdgm-rev__body")?.textContent) || (photo ? "Photo review." : ""),
      verified: node.getAttribute("data-verified-buyer") === "true" || Boolean(node.querySelector(".jdgm-rev__buyer-badge")),
      date: dateText(timestamp),
      timestamp: parseDateValue(timestamp),
      productTitle,
      productUrl: productUrl ? (productUrl.startsWith("http") ? productUrl : new URL(productUrl, window.location.origin).href) : "/collections/all-products",
      productImage: "",
      photo,
    };
  };

  const unescapeJudgeMeHtml = (html) => String(html || "")
    .replace(/\\u003c/g, "<")
    .replace(/\\u003e/g, ">")
    .replace(/\\u0027/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\\\//g, "/")
    .replace(/\\u0026/g, "&");

  const readAttr = (html, pattern) => {
    const match = html.match(pattern);
    return match ? decodeHtml(match[1]) : "";
  };

  const normalizePhotoReviewHtml = (html) => {
    const photo = largePhotoUrl(readAttr(html, /(?:data-src|src|href)='(https:\/\/review-images\.judgeme\.com[^']+)'/i).replace(/&amp;/g, "&"));
    if (!photo) return null;
    const author = htmlText(readAttr(html, /<span class='jdgm-rev__author'[^>]*>([\s\S]*?)<\/span>/i));
    const initial = htmlText(readAttr(html, /<div class='jdgm-rev__icon'[^>]*>([\s\S]*?)<\/div>/i)) || author.slice(0, 1) || "N";
    const productTitle = htmlText(readAttr(html, /<a[^>]*class='jdgm-rev__prod-link'[^>]*>([\s\S]*?)<\/a>/i)) || "NASHE";
    const productUrl = readAttr(html, /<a href='([^']+)'[^>]*class='jdgm-rev__prod-link'/i) || "/pages/reviews";
    const body = htmlText(readAttr(html, /<div class='jdgm-rev__body'[^>]*>([\s\S]*?)<\/div>/i));

    return {
      id: readAttr(html, /data-review-id='([^']+)'/i) || photo,
      author: author || "Verified buyer",
      initial,
      rating: Number(readAttr(html, /class='jdgm-rev__rating' data-score='([^']+)'/i)) || 5,
      body: body || "Photo review.",
      verified: /data-verified-buyer='true'/i.test(html) || /jdgm-rev__buyer-badge/i.test(html),
      date: dateText(readAttr(html, /data-content='([^']+)'/i)),
      timestamp: parseDateValue(readAttr(html, /data-content='([^']+)'/i)),
      productTitle,
      productUrl: productUrl.startsWith("http") ? productUrl : new URL(productUrl, window.location.origin).href,
      productImage: "",
      photo,
    };
  };

  const normalizeLegacyReviewHtml = (html) => {
    const author = htmlText(readAttr(html, /<span class='jdgm-rev__author'[^>]*>([\s\S]*?)<\/span>/i));
    const initial = htmlText(readAttr(html, /<div class='jdgm-rev__icon'[^>]*>([\s\S]*?)<\/div>/i)) || author.slice(0, 1) || "N";
    const productTitle = htmlText(readAttr(html, /<a[^>]*class='jdgm-rev__prod-link'[^>]*>([\s\S]*?)<\/a>/i)) || "NASHE";
    const productUrl = readAttr(html, /<a href='([^']+)'[^>]*class='jdgm-rev__prod-link'/i) || "/collections/all-products";
    const body = htmlText(readAttr(html, /<div class='jdgm-rev__body'[^>]*>([\s\S]*?)<\/div>/i));
    const photo = largePhotoUrl(readAttr(html, /(?:data-src|src|href)='(https:\/\/review-images\.judgeme\.com[^']+)'/i).replace(/&amp;/g, "&"));
    const timestamp = readAttr(html, /data-content='([^']+)'/i);

    if (!body && !photo) return null;

    return {
      id: readAttr(html, /data-review-id='([^']+)'/i) || `${author}-${timestamp}-${productTitle}-${photo}`,
      author: author || "Verified buyer",
      initial,
      rating: Number(readAttr(html, /class='jdgm-rev__rating' data-score='([^']+)'/i)) || 5,
      body: body || (photo ? "Photo review." : ""),
      verified: /data-verified-buyer='true'/i.test(html) || /jdgm-rev__buyer-badge/i.test(html),
      date: dateText(timestamp),
      timestamp: parseDateValue(timestamp),
      productTitle,
      productUrl: productUrl.startsWith("http") ? productUrl : new URL(productUrl, window.location.origin).href,
      productImage: "",
      photo,
    };
  };

  const legacyReviewsFromRawHtml = (html) => {
    const normalized = unescapeJudgeMeHtml(html);
    return Array.from(normalized.matchAll(/<div class='jdgm-rev[\s\S]*?(?=<div class='jdgm-rev|$)/g))
      .map((match) => match[0])
      .map(normalizeLegacyReviewHtml)
      .filter(Boolean);
  };

  const loadLegacyReviews = (root) => {
    const source = root.querySelector("[data-nashe-jr-legacy-source]");
    if (!source) return [];
    const domReviews = Array.from(source.querySelectorAll(".jdgm-rev"))
      .map(normalizeLegacyReviewNode)
      .filter((review) => review.body || review.photo);
    const rawReviews = legacyReviewsFromRawHtml(source.innerHTML);
    return uniqueReviews([...domReviews, ...rawReviews]);
  };

  const photoReviewsFromRawHtml = (html) => {
    const normalized = unescapeJudgeMeHtml(html);
    return Array.from(normalized.matchAll(/<div class='jdgm-rev[\s\S]*?(?=<div class='jdgm-rev|$)/g))
      .map((match) => match[0])
      .filter((chunk) => /review-images\.judgeme\.com|jdgm-rev__pic-img/i.test(chunk))
      .map(normalizePhotoReviewHtml)
      .filter(Boolean);
  };

  const loadPhotoReviewsFromReviewsPage = async (root) => {
    const url = root.dataset.readMoreUrl || "/pages/reviews";
    const response = await fetch(url, { credentials: "omit", cache: "no-store" });
    if (!response.ok) return [];
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const domReviews = Array.from(doc.querySelectorAll(".jdgm-rev"))
      .map(normalizePhotoReviewNode)
      .filter((review) => review.photo);
    const rawReviews = photoReviewsFromRawHtml(html);
    return uniqueReviews([...domReviews, ...rawReviews]
      .filter((review, index, all) => all.findIndex((item) => item.id === review.id || photoKey(item.photo) === photoKey(review.photo)) === index));
  };

  const card = (review) => {
    const media = review.photo
      ? `<div class="nashe-jr__media"><a class="nashe-jr__photo" href="${esc(review.photo)}" target="_blank" rel="nofollow noopener"><img src="${esc(review.photo)}" alt=""></a></div>`
      : "";
    const image = review.productImage
      ? `<span class="nashe-jr__product-image"><img src="${esc(review.productImage)}" alt=""></span>`
      : `<span class="nashe-jr__product-image" aria-hidden="true"></span>`;
    const productRowClass = review.productImage ? "nashe-jr__product-row" : "nashe-jr__product-row nashe-jr__product-row--no-image";

    return `
      <article class="nashe-jr__card${review.photo ? " nashe-jr__card--photo" : ""}">
        <div class="nashe-jr__card-main">
          <div class="nashe-jr__card-head">
            <span class="nashe-jr__avatar" aria-hidden="true">${esc(review.initial.slice(0, 1))}</span>
            <div>
              <div class="nashe-jr__author-row">
                <span class="nashe-jr__author">${esc(review.author)}</span>
                ${review.verified ? '<span class="nashe-jr__verified">Verified</span>' : ""}
              </div>
              ${review.date ? `<span class="nashe-jr__product-label">${esc(review.date)}</span>` : ""}
            </div>
          </div>
          <div class="nashe-jr__card-stars" aria-label="${esc(review.rating)} out of 5 stars">${esc(stars(review.rating))}</div>
          <p class="nashe-jr__text">${esc(review.body || "Srce zna odakle je.")}</p>
          ${media}
        </div>
        <div class="${productRowClass}">
          ${image}
          <span>
            <span class="nashe-jr__product-label">Product</span>
            <a class="nashe-jr__product-title" href="${esc(review.productUrl)}">${esc(review.productTitle)}</a>
          </span>
        </div>
      </article>
    `;
  };

  const updateTabs = (root, mode) => {
    root.querySelectorAll("[data-nashe-jr-tab]").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.nasheJrTab === mode);
    });
  };

  const pageSizeFor = (root) => root.dataset.reviewLimit === "all"
    ? 12
    : Math.max(1, Number(root.dataset.reviewLimit) || 16);

  const paginationMarkup = (currentPage, totalPages) => {
    if (totalPages <= 1) return "";
    const pageButton = (page, label, extra = "") => `<button class="nashe-jr__page-btn${page === currentPage ? " is-active" : ""}${extra}" type="button" data-nashe-jr-page="${page}"${page === currentPage ? ' aria-current="page"' : ""}>${label}</button>`;
    const buttons = [`<button class="nashe-jr__page-btn" type="button" data-nashe-jr-page="${currentPage - 1}" aria-label="Previous page"${currentPage === 1 ? " disabled" : ""}>&#8592;</button>`];
    const numbers = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])].filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    let previous = 0;
    numbers.forEach(page => {
      if (previous && page > previous + 1) buttons.push('<span class="nashe-jr__page-gap" aria-hidden="true">...</span>');
      buttons.push(pageButton(page, page, page !== 1 && page !== totalPages && page !== currentPage ? " nashe-jr__page-btn--neighbor" : ""));
      previous = page;
    });
    buttons.push(`<button class="nashe-jr__page-btn" type="button" data-nashe-jr-page="${currentPage + 1}" aria-label="Next page"${currentPage === totalPages ? " disabled" : ""}>&#8594;</button>`);
    return `<nav class="nashe-jr__pagination" aria-label="Review pages">${buttons.join("")}</nav>`;
  };

  const render = (root, state, mode, page = 1) => {
    const grid = root.querySelector("[data-nashe-jr-grid]");
    const pager = root.querySelector("[data-nashe-jr-pagination]");
    const reviews = mode === "photos" ? state.photoReviews : state.productReviews;
    const size = pageSizeFor(root);
    const knownPages = Math.max(1, Math.ceil(reviews.length / size));
    const totalPages = root.dataset.reviewLimit !== "all" ? 1 : state.complete ? knownPages
      : mode === "photos" ? Math.max(knownPages, page + 1)
      : Math.max(knownPages, Math.ceil(state.estimatedCount / size));
    const currentPage = Math.max(1, Math.min(Number(page) || 1, totalPages));
    const visible = reviews.slice((currentPage - 1) * size, currentPage * size);
    state.mode = mode;
    state.pageByMode[mode] = currentPage;
    root.classList.toggle("nashe-jr--photos", mode === "photos");
    updateTabs(root, mode);
    grid.innerHTML = visible.length ? visible.map(card).join("")
      : `<div class="nashe-jr__empty">${esc(state.busy ? "Loading reviews..." : state.error ? "Reviews could not load. Please try again." : root.dataset.emptyText)}</div>`;
    if (pager) {
      pager.innerHTML = paginationMarkup(currentPage, totalPages);
      pager.hidden = totalPages <= 1;
    }
    grid.setAttribute("aria-busy", String(state.busy));
  };

  const fetchReviewsPage = async ({ token, shopDomain, page, perPage }) => {
    const params = new URLSearchParams({ api_token: token, shop_domain: shopDomain, json_request: "true", per_page: String(perPage), page: String(page) });
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(`https://judge.me/api/v1/widgets/all_reviews_page?${params}`, { credentials: "omit", signal: controller.signal });
      if (!response.ok) throw new Error(`Judge.me request failed: ${response.status}`);
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  };

  const load = async (root) => {
    const token = root.dataset.apiToken;
    const shopDomain = root.dataset.shopDomain;
    if (!token || !shopDomain) throw new Error("Missing Judge.me public API settings");
    let pager = root.querySelector("[data-nashe-jr-pagination]");
    if (!pager) {
      pager = document.createElement("div");
      pager.className = "nashe-jr__pagination-wrap";
      pager.setAttribute("data-nashe-jr-pagination", "");
      root.querySelector("[data-nashe-jr-grid]").after(pager);
    }
    const cacheKey = `nashe-reviews-v2:${shopDomain}:${document.documentElement.lang}:${root.dataset.storeCount || ""}`;
    let cached = null;
    try {
      const value = JSON.parse(sessionStorage.getItem(cacheKey) || "null");
      if (value && Date.now() - value.time < 300000 && Array.isArray(value.pages)) cached = value;
    } catch {}
    const legacy = loadLegacyReviews(root);
    const state = {
      pages: cached?.pages || [], nextApiPage: cached?.nextApiPage || 1,
      totalApiPages: cached?.totalApiPages || 0, complete: cached?.complete || false,
      estimatedCount: Number(root.dataset.storeCount) || 24,
      productReviews: [], photoReviews: [], mode: "product", pageByMode: { product: 1, photos: 1 },
      busy: false, error: false, queue: Promise.resolve(), request: 0,
    };
    const refresh = () => {
      state.productReviews = sortReviewsByDate(uniqueReviews([...state.pages.flat(), ...legacy]));
      state.photoReviews = sortReviewsByDate(uniqueReviews(state.productReviews.filter(review => review.photo)));
      const avg = Number(root.dataset.storeRating) || (state.productReviews.length ? state.productReviews.reduce((sum, review) => sum + review.rating, 0) / state.productReviews.length : 5);
      const count = Number(root.dataset.storeCount) || state.productReviews.length;
      const summary = root.querySelector("[data-nashe-jr-summary]");
      if (summary) summary.innerHTML = `<div class="nashe-jr__stars" aria-label="${avg.toFixed(2)} out of 5 stars">${esc(stars(avg))}</div><div class="nashe-jr__summary-text">${avg.toFixed(2)} / 5 &#183; ${count} verified reviews. Narod zna.</div>`;
    };
    const save = () => {
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), pages: state.pages, nextApiPage: state.nextApiPage, totalApiPages: state.totalApiPages, complete: state.complete }));
      } catch {}
    };
    const next = async () => {
      const data = await fetchReviewsPage({ token, shopDomain, page: state.nextApiPage, perPage: 24 });
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];
      state.totalApiPages = Math.max(0, Number(data.total_pages) || 0);
      state.pages.push(reviews.filter(isProductReview).map(normalize).filter(review => review.body || review.photo));
      state.nextApiPage += 1;
      state.complete = !reviews.length || (state.totalApiPages ? state.nextApiPage > state.totalApiPages : reviews.length < 24) || state.nextApiPage > 80;
      refresh();
      save();
    };
    const ensure = (mode, page, first = false) => {
      const operation = state.queue.catch(() => {}).then(async () => {
        if (first && state.nextApiPage === 1) await next();
        const required = page * pageSizeFor(root);
        while (!state.complete && (mode === "photos" ? state.photoReviews.length : state.pages.flat().length) < required) await next();
      });
      state.queue = operation;
      return operation;
    };
    const navigate = async (mode, page, scroll = false) => {
      const request = ++state.request;
      state.busy = true;
      state.error = false;
      render(root, state, mode, page);
      try {
        await ensure(mode, page);
      } catch {
        state.error = true;
      }
      if (request !== state.request) return;
      state.busy = false;
      render(root, state, mode, page);
      if (scroll) root.querySelector("[data-nashe-jr-grid]")?.scrollIntoView({ block: "start", behavior: "smooth" });
    };
    pager.addEventListener("click", event => {
      const button = event.target.closest("[data-nashe-jr-page]");
      if (button && !button.disabled) navigate(state.mode, Number(button.dataset.nasheJrPage), true);
    });
    root.querySelectorAll("[data-nashe-jr-tab]").forEach(button => {
      button.addEventListener("click", () => {
        const mode = button.dataset.nasheJrTab;
        navigate(mode, state.pageByMode[mode] || 1);
      });
    });
    refresh();
    state.busy = !state.productReviews.length;
    render(root, state, "product");
    try {
      await ensure("product", 1, true);
    } catch {
      state.error = true;
    }
    if (!state.request) {
      state.busy = false;
      render(root, state, "product");
    }
  };


  roots.forEach((root) => {
    load(root).catch(() => {
      const grid = root.querySelector("[data-nashe-jr-grid]");
      grid.innerHTML = `<div class="nashe-jr__empty">${esc(root.dataset.emptyText)}</div>`;
    });
  });
})();
